/**
 * TemplateForm — the template create / edit fields. Demo page: /form-layouts (sections of a two-column form).
 * The schema starts from the generated `AgentTemplateUpsert` body; the slug (a path parameter of the request) is the
 * only UI-only field. Field help is the shared `TEMPLATE_FIELD_HELP` map: the (?) popover and the Field Help panel read
 * the same text.
 */
import React from 'react';
import { useController } from 'react-hook-form';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { listToolsOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { zAgentTemplateUpsert } from '../../generated/api/zod.gen.js';
import type { AgentTemplate, AgentTemplateUpsert } from '../../generated/api/types.gen.js';
import {
  FormFullWidth,
  FormSection,
  MultiSelectFormField,
  SelectFormField,
  SwitchFormField,
  TextAreaFormField,
  TextFormField,
} from '../../components/form/index.js';
import { TEMPLATE_FIELD_HELP } from '../../help/template-fields.js';
import { RoutingRulesEditor } from './RoutingRulesEditor.js';
import { HelpField } from './entity-ui/FieldHelpAside.js';
import { sentenceCase } from '../../components/ui/FilterSelect.js';

export const TEMPLATE_DEPARTMENTS = ['research', 'risk', 'execution', 'compliance', 'orchestration'] as const;
export const TEMPLATE_PERMISSION_TIERS = ['observer', 'contributor', 'operator'] as const;

const DEPARTMENT_OPTIONS = TEMPLATE_DEPARTMENTS.map((value) => ({ value, label: sentenceCase(value) }));
const TIER_OPTIONS = TEMPLATE_PERMISSION_TIERS.map((value) => ({ value, label: sentenceCase(value) }));

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** The generated upsert body, plus the slug the request carries in its path. */
export const templateFormSchema = zAgentTemplateUpsert
  .pick({
    title: true,
    departmentId: true,
    goal: true,
    backstory: true,
    instructions: true,
    constraints: true,
    tools: true,
    permissionTier: true,
    platform: true,
    disableModelInvocation: true,
    routingRules: true,
  })
  .extend({
    slug: z.string().min(1).regex(SLUG_PATTERN, 'Use lowercase letters, numbers and hyphens only'),
    title: z.string().min(1),
  });

export type TemplateFormValues = z.output<typeof templateFormSchema>;

export const EMPTY_TEMPLATE_VALUES: TemplateFormValues = {
  slug: '',
  title: '',
  departmentId: 'research',
  goal: '',
  backstory: '',
  instructions: '',
  constraints: '',
  tools: [],
  permissionTier: 'observer',
  platform: '',
  disableModelInvocation: false,
  routingRules: [],
};

/** Form values for an existing template (the edit form's defaults). */
export function templateToFormValues(template: AgentTemplate): TemplateFormValues {
  return {
    slug: template.slug,
    title: template.title,
    departmentId: template.departmentId ?? 'research',
    goal: template.goal ?? '',
    backstory: template.backstory ?? '',
    instructions: template.instructions ?? '',
    constraints: template.constraints ?? '',
    tools: template.tools ?? [],
    permissionTier: template.permissionTier ?? 'observer',
    platform: template.platform ?? '',
    disableModelInvocation: template.disableModelInvocation ?? false,
    routingRules: template.routingRules ?? [],
  };
}

/** The request body for the form values (the slug goes in the path). */
export function formValuesToUpsert(values: TemplateFormValues): AgentTemplateUpsert {
  const { slug: _slug, platform, ...body } = values;
  return { ...body, platform: platform || undefined };
}

/**
 * The assistant reads and writes the form in its own vocabulary (`department`, `tools` as one comma-separated string).
 * These two adapters translate in both directions so its apply pills keep working.
 */
export function templateAssistantValues(values: TemplateFormValues): Record<string, unknown> {
  const { departmentId, tools, ...rest } = values;
  return { ...rest, department: departmentId, tools: (tools ?? []).join(', ') };
}

export function templateAssistantPatch(partial: Record<string, unknown>): Partial<TemplateFormValues> {
  const patch: Partial<TemplateFormValues> = {};
  const text = (key: string): string | undefined =>
    typeof partial[key] === 'string' ? String(partial[key]) : undefined;
  for (const key of ['slug', 'title', 'goal', 'backstory', 'instructions', 'constraints', 'platform'] as const) {
    const value = text(key);
    if (value !== undefined) patch[key] = value;
  }
  const department = text('department') ?? text('departmentId');
  if (department !== undefined) patch.departmentId = department;
  const tier = text('permissionTier');
  if (tier !== undefined) patch.permissionTier = tier;
  const tools = partial.tools;
  if (typeof tools === 'string') {
    patch.tools = tools
      .split(',')
      .map((tool) => tool.trim())
      .filter(Boolean);
  } else if (Array.isArray(tools)) {
    patch.tools = tools.filter((tool): tool is string => typeof tool === 'string');
  }
  if (typeof partial.disableModelInvocation === 'boolean')
    patch.disableModelInvocation = partial.disableModelInvocation;
  return patch;
}

interface TemplateFieldsProps {
  /** `/new`: the slug can be typed. Edit: it is fixed. */
  slugEditable: boolean;
  disableModelInvocation: boolean;
}

/** Orchestrator routing rules in the form (the editor owns its own inputs; the form owns the array). */
const RoutingRulesField: React.FC = () => {
  const { field } = useController<TemplateFormValues, 'routingRules'>({ name: 'routingRules' });
  return <RoutingRulesEditor rules={field.value ?? []} onChange={field.onChange} />;
};

export const TemplateFields: React.FC<TemplateFieldsProps> = ({ slugEditable, disableModelInvocation }) => {
  const toolsQuery = useQuery(listToolsOptions());
  const toolOptions = (toolsQuery.data?.data ?? []).map((tool) => ({ value: tool.name, label: tool.name }));
  const { field: toolsField } = useController<TemplateFormValues, 'tools'>({ name: 'tools' });
  // A template can already name a tool the registry no longer lists; keep it selectable so saving does not drop it.
  const known = new Set(toolOptions.map((o) => o.value));
  const options = [
    ...toolOptions,
    ...(toolsField.value ?? []).filter((t) => !known.has(t)).map((t) => ({ value: t, label: t })),
  ];

  return (
    <>
      <FormSection title="Identity">
        <HelpField name="title">
          <TextFormField
            name="title"
            label="Title"
            placeholder="Macro analyst"
            help={TEMPLATE_FIELD_HELP.title}
            required
          />
        </HelpField>
        <HelpField name="slug">
          <TextFormField
            name="slug"
            label="Slug"
            placeholder="macro-analyst"
            description={
              slugEditable
                ? 'Workflow steps reference the template by this. It cannot change after creation.'
                : 'Fixed after creation.'
            }
            help={TEMPLATE_FIELD_HELP.slug}
            disabled={!slugEditable}
            required
          />
        </HelpField>
        <HelpField name="department">
          <SelectFormField
            name="departmentId"
            label="Department"
            options={DEPARTMENT_OPTIONS}
            help={TEMPLATE_FIELD_HELP.department}
            required
          />
        </HelpField>
        <HelpField name="permissionTier">
          <SelectFormField
            name="permissionTier"
            label="Permission tier"
            options={TIER_OPTIONS}
            help={TEMPLATE_FIELD_HELP.permissionTier}
            required
          />
        </HelpField>
        <HelpField name="platform">
          <TextFormField
            name="platform"
            label="Platform"
            placeholder="youtube, twitter, arxiv"
            description="Optional. Leave empty for a template that works with any source."
            help={TEMPLATE_FIELD_HELP.platform}
          />
        </HelpField>
      </FormSection>

      <FormSection title="Behavior">
        <FormFullWidth>
          <HelpField name="goal">
            <TextFormField
              name="goal"
              label="Goal"
              placeholder="Surface macro shifts that change risk assumptions"
              help={TEMPLATE_FIELD_HELP.goal}
            />
          </HelpField>
        </FormFullWidth>
        <FormFullWidth>
          <HelpField name="backstory">
            <TextAreaFormField
              name="backstory"
              label="Backstory"
              rows={3}
              placeholder="What expertise and voice this agent has"
              help={TEMPLATE_FIELD_HELP.backstory}
            />
          </HelpField>
        </FormFullWidth>
        <FormFullWidth>
          <HelpField name="instructions">
            <TextAreaFormField
              name="instructions"
              label="Instructions"
              rows={8}
              placeholder="The main prompt. Markdown works; use {{variable}} for parameters."
              help={TEMPLATE_FIELD_HELP.instructions}
            />
          </HelpField>
        </FormFullWidth>
        <FormFullWidth>
          <HelpField name="constraints">
            <TextAreaFormField
              name="constraints"
              label="Constraints"
              rows={3}
              placeholder="Rules this agent must never break"
              help={TEMPLATE_FIELD_HELP.constraints}
            />
          </HelpField>
        </FormFullWidth>
      </FormSection>

      <FormSection title="Tools and routing">
        <FormFullWidth>
          <HelpField name="tools">
            <MultiSelectFormField
              name="tools"
              label="Tools"
              options={options}
              placeholder={
                toolsQuery.isError ? 'The tool list did not load' : toolsQuery.isPending ? 'Loading tools' : 'No tools'
              }
              description={
                toolsQuery.isError
                  ? 'The tool list did not load: only the tools already chosen are listed. Reload the page to pick others.'
                  : 'The tools this agent may call.'
              }
              help={TEMPLATE_FIELD_HELP.tools}
            />
          </HelpField>
        </FormFullWidth>
        <FormFullWidth>
          <HelpField name="disableModelInvocation">
            <SwitchFormField
              name="disableModelInvocation"
              label="Pure orchestrator"
              description="Route tasks by rule and skip the model entirely."
            />
          </HelpField>
        </FormFullWidth>
        {disableModelInvocation && (
          <FormFullWidth>
            <RoutingRulesField />
          </FormFullWidth>
        )}
      </FormSection>
    </>
  );
};
